import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

// Logos
const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function TableNo25() {
  const pdfRef = useRef();

  // 📊 Male Age Group Data (2024)
  const ageData = [
    { ageGroup: "14–18", maleCount: 920 },
    { ageGroup: "19–21", maleCount: 3248 },
    { ageGroup: "22–24", maleCount: 5180 },
    { ageGroup: "25–34", maleCount: 21678 },
    { ageGroup: "35–44", maleCount: 21908 },
    { ageGroup: "45–54", maleCount: 19361 },
    { ageGroup: "55–64", maleCount: 14970 },
    { ageGroup: "65–99", maleCount: 5676 },
  ];

  // 📈 Insights
  const totalMaleCount = ageData.reduce((sum, d) => sum + d.maleCount, 0);
  const topGroup = ageData.reduce((max, d) =>
    d.maleCount > max.maleCount ? d : max,
  );
  const lowGroup = ageData.reduce((min, d) =>
    d.maleCount < min.maleCount ? d : min,
  );

  // Color palette per age group
  const colors = [
    "#FFA1B5", // 14–18
    "#86C7F3", // 19–21
    "#FFE29A", // 22–24
    "#93D9D9", // 25–34
    "#C2A3FF", // 35–44
    "#FFC58C", // 45–54
    "#B9D2AA", // 55–64
    "#6794D1", // 65–99
  ];
  // 📊 Chart setup
  const chartData = {
    labels: ageData.map((d) => d.ageGroup),
    datasets: [
      {
        label: "Male Count (2024)",
        data: ageData.map((d) => d.maleCount),
        backgroundColor: colors,
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: { color: "#111827", font: { size: 13, weight: "bold" } },
      },
      title: {
        display: true,
        text: "📊 Male Count by Age Group (2024)",
        color: "#111827",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.parsed.y.toLocaleString("en-US")} males`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (v) => v.toLocaleString("en-US"), color: "#4B5563" },
        title: {
          display: true,
          text: "Male Count",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
      x: {
        ticks: { color: "#4B5563" },
        title: {
          display: true,
          text: "Age Groups",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // 📄 PDF Export
    const downloadPDF = () => {
      const input = pdfRef.current;
      html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 10;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;
        let pageNumber = 1;

        const addHeader = () => {
          const logoWidth = 20; // ideal header width
          const logoHeight = 20; // adjust ratio if needed

          pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
          pdf.setFontSize(26);
          pdf.text("UTahData.org", 95, 22);
          pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
          pdf.line(10, 35, 200, 35);
        };

        const addFooter = (pageNumber) => {
          pdf.setFontSize(9);
          const footerY = pageHeight - 10;
          pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
          pdf.text(
            `Downloaded on: ${new Date().toLocaleDateString()}`,
            10,
            footerY,
          );
          pdf.text(
            `Page ${pageNumber}`,
            pageWidth - 10,
            footerY,
            null,
            null,
            "right",
          );
        };

        // First page
        addHeader();
        pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;

        // Add more pages if needed
        while (heightLeft > 0) {
          pdf.addPage();
          pageNumber++;
          addHeader();
          position = heightLeft - imgHeight;
          pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
          heightLeft -= pageHeight - 32;
          addFooter(pageNumber);
        }

        // Final footer
        // ===== FINAL FOOTER SAFE AREA =====

        const footerTopY = pageHeight - 40; // start footer 40mm from bottom
        const footerContentY = pageHeight - 28; // logos row
        const footerBottomTextY = pageHeight - 10; // bottom text row

        // Top Divider Line
        pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

        // UtahData logo
        const utahWidth = 18;
        const utahHeight = 18;
        pdf.addImage(
          utahData,
          "PNG",
          20,
          footerContentY - 8,
          utahWidth,
          utahHeight,
        );

        // Built by
        pdf.setFontSize(10);
        pdf.text("Built by", 45, footerContentY);

        // BODEX logo
        const bodexWidth = 36;
        const bodexHeight = bodexWidth * 0.3;
        pdf.addImage(
          bodex,
          "PNG",
          60,
          footerContentY - 6,
          bodexWidth,
          bodexHeight,
        );

        // Using text
        pdf.text("using", 102, footerContentY);

        // KPI logo
        pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

        // Version
        pdf.text(APP_VERSION, 140, footerContentY);

        // Bottom Info Line
        pdf.setFontSize(8);
        pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          pageWidth - 10,
          footerBottomTextY,
          null,
          null,
          "right",
        );

        pdf.save("male_count_by_age_group.pdf");
        // input.style.width = originalWidth;
      });
    };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-indigo-50 rounded-xl shadow-lg"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Male Count by Age Group (2024)
        </h2>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Total Male Count</p>
            <p className="text-xl font-bold text-blue-800">
              {totalMaleCount.toLocaleString("en-US")}
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Highest Count</p>
            <p className="text-xl font-bold text-green-700">
              {topGroup.ageGroup} ({topGroup.maleCount.toLocaleString("en-US")})
            </p>
          </div>
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Lowest Count</p>
            <p className="text-xl font-bold text-red-700">
              {lowGroup.ageGroup} ({lowGroup.maleCount.toLocaleString("en-US")})
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto rounded-lg shadow-md">
            <table className="w-full border-collapse text-center">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  <th className="p-2">Male Count (2024)</th>
                  <th className="p-2">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {ageData.map((row, i) => {
                  const share = (
                    (row.maleCount / totalMaleCount) *
                    100
                  ).toFixed(1);
                  const isTop = row.ageGroup === topGroup.ageGroup;
                  const isLow = row.ageGroup === lowGroup.ageGroup;
                  return (
                    <tr
                      key={i}
                      className={`even:bg-gray-50 odd:bg-white hover:bg-gray-100 ${
                        isTop
                          ? "bg-green-100 font-semibold"
                          : isLow
                            ? "bg-red-100 font-semibold"
                            : ""
                      }`}
                    >
                      <td className="p-2">{row.ageGroup}</td>
                      <td className="p-2 text-blue-700">
                        {row.maleCount.toLocaleString("en-US")}
                      </td>
                      <td
                        className={`p-2 ${
                          isTop ? "text-green-700" : isLow ? "text-red-700" : ""
                        }`}
                      >
                        {share}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="text-center mt-4 text-gray-700">
          <p className="text-sm">
            📌 The{" "}
            <span className="font-semibold text-green-700">
              {topGroup.ageGroup}
            </span>{" "}
            group has the highest male count, while{" "}
            <span className="font-semibold text-red-700">
              {lowGroup.ageGroup}
            </span>{" "}
            records the lowest share of total employment.
          </p>
        </div>
      </div>

      {/* PDF Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
